// @ts-nocheck
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

import { AlertCircle, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

const MISSING_LABELS: Record<string, string> = {
  email: 'No email',
  grad_year: 'No grad year',
  position: 'No position',
};

function relativeDate(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const diff = Date.now() - d.getTime();
  const day = 24 * 60 * 60 * 1000;
  const days = Math.floor(diff / day);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

export function MissingInfoIcons({ missing }: { missing?: string[] }) {
  if (!missing || missing.length === 0) return null;
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      {missing
        .filter((m) => MISSING_LABELS[m])
        .map((m) => (
          <span
            key={m}
            className="inline-flex items-center gap-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 text-[10px] font-medium leading-none"
          >
            <AlertCircle className="h-3 w-3" />
            {MISSING_LABELS[m]}
          </span>
        ))}
    </span>
  );
}

const CLAIM_BADGES: Record<string, { label: string; cls: string }> = {
  claimed: { label: 'Claimed', cls: 'bg-green-100 text-green-800 hover:bg-green-100 border-green-200' },
  invited: { label: 'Invited', cls: 'bg-blue-100 text-blue-800 hover:bg-blue-100 border-blue-200' },
  not_invited: { label: 'Not invited', cls: 'bg-muted text-muted-foreground' },
  no_contact: { label: 'No email', cls: 'bg-muted text-muted-foreground' },
};

export function ClaimControls({ player, onChanged }: { player: any; onChanged?: () => void }) {
  const [sending, setSending] = useState(false);
  const state = player.claim_state ?? 'not_invited';
  const badge = CLAIM_BADGES[state] ?? CLAIM_BADGES.not_invited;
  const showButton = state === 'invited' || state === 'not_invited';
  const buttonLabel = state === 'invited' ? 'Resend' : 'Send invite';
  const sentRel = relativeDate(player.claim_invite_sent_at);

  const sendInvite = async () => {
    setSending(true);
    try {
      const { data, error } = await invokeEdgeFunction('rle-send-player-claim-invite', {
        body: { player_id: player.player_id, method: 'email', target: 'athlete' },
      });
      if (error) throw new Error(error.message ?? 'Request failed');
      if (data?.ok === false) throw new Error(data.error?.message ?? 'Request failed');
      toast.success('Invitation sent');
      onChanged?.();
    } catch (e: any) {
      toast.error(e.message ?? 'Failed to send invitation');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <div className="flex flex-col items-end leading-tight">
        <Badge variant="outline" className={`${badge.cls} text-[10px] px-1.5 py-0`}>{badge.label}</Badge>
        {state === 'invited' && sentRel && (
          <span className="text-[10px] text-muted-foreground">sent {sentRel}</span>
        )}
      </div>
      {showButton && (
        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={sending} onClick={sendInvite}>
          {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
          {buttonLabel}
        </Button>
      )}
    </div>
  );
}
